import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  Typography,
  IconButton,
  styled,
  TextField,
  MultiLineTextField,
} from 'drift-ui';
import { CloseOutlined } from '@mui/icons-material';
import Icon from './Icon';
import { CarrierAccesPayload } from '../features/findLoads/permissionsType';
import { Controller, useForm } from 'react-hook-form';
import {
  useGetCarrierPermissionsQuery,
  useRequestCarrierAccessMutation,
  onAccessRequested,
  closePermisssionDialog,
} from '../features/findLoads/permissionsState';
import { getCurrentUser, getPersmissionsLevel } from '../utilities/authUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../configureStore';

interface FormData {
  companyAdminName: string;
  companyAdminEmail: string;
  permissionLevel: string;
  userFreightPermission: string;
  comments: string;
}

const helperText = (error: boolean) => ({
  fontSize: '12px',
  color: error ? '#EF3824' : '#666666',
});

const containerStyles = (upgradedAccess: boolean) => ({
  '& .MuiDialog-paper': {
    width: '746px',
    height: upgradedAccess ? '383px' : '506px',
    maxWidth: 'unset',
  },
});

const UserForm = styled(Box)`
  display: flex;
  margin-top: 24px;
  gap: 16px;
`;

const multiLineStyles = {
  '& .MuiOutlinedInput-input': { p: '0px !important' },
  '& .MuiInputBase-input': { p: '0px !important' },
  '& .MuiInputBase-input::placeholder': { opacity: 0.5 },
  '& .MuiInputBase-input:focus::placeholder': {
    opacity: 0,
  },
};

const buttonsContainer = {
  display: 'flex',
  justifyContent: 'end',
  gap: '16px',
  marginTop: '16px',
  marginRight: '8px',
};

const UserPermissionsLevelForm = () => {
  const dispatch = useDispatch();
  const { dialogOpen, config } = useSelector(
    (state: RootState) => state.permissionDialog
  );
  const [loading, setLoading] = useState(false);
  const [showSuccessUpgrade, setShowSuccessUpgrade] = useState(false);

  const currentUser = getCurrentUser();
  const { data } = useGetCarrierPermissionsQuery(
    currentUser?.mcleod_carrier_id
  );

  const [requesCarrierAccess] = useRequestCarrierAccessMutation();

  const permissionLevel = data && getPersmissionsLevel(data.companyPermissions);

  const { control, handleSubmit, watch, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      permissionLevel,
      companyAdminName: data && data.companyAdminName,
      companyAdminEmail: data && data.companyAdminEmail,
      userFreightPermission: 'No Freight',
      comments: '',
    },
  });

  let titleText: string = 'Requesting upgraded Freight access.';

  const sendForm = async (formData: FormData) => {
    setLoading(true);

    const payload: CarrierAccesPayload = {
      userEmail: currentUser?.email!,
      permissionRequested: permissionLevel!,
      currentPermission: 'No Freight',
      primaryEmail: data?.companyAdminEmail!,
      comments: formData.comments,
    };

    await requesCarrierAccess(payload);

    if (config.formType === 'upgrade') {
      titleText = 'Upgraded load board access requested.';
      setShowSuccessUpgrade(true);
      setLoading(false);
      dispatch(onAccessRequested(true));
    } else {
      globalThis.window.location.reload();
    }
  };

  const watchComments = watch('comments') || '';

  const errorCommentsLength = watchComments.length > 250;

  const handleDialogOnClosed = () => {
    reset({ comments: '' });
    setShowSuccessUpgrade(false);
    dispatch(closePermisssionDialog());
  };

  return (
    <Dialog
      open={dialogOpen}
      sx={containerStyles(showSuccessUpgrade)}
      hideBackdrop={config.hideBackdrop}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ '&.MuiTypography-root': { fontWeight: 600 } }}>
        {titleText}
      </DialogTitle>
      <IconButton
        onClick={handleDialogOnClosed}
        sx={{
          position: 'absolute',
          right: 26,
          top: 18,
          color: '#666666',
        }}
      >
        <CloseOutlined />
      </IconButton>

      {!showSuccessUpgrade ? (
        <DialogContent>
          <Typography sx={{ color: '#333333' }}>
            This request goes directly to your company admin. Include any
            comments that will help them verify and approve your new access
            level.{' '}
          </Typography>
          <form onSubmit={handleSubmit(sendForm)}>
            <UserForm>
              <Box
                sx={{
                  width: '337px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Controller
                  name="companyAdminName"
                  control={control}
                  render={() => (
                    <TextField
                      value={data && data.companyAdminName}
                      disabled
                      label="Company admin name"
                    />
                  )}
                />
                <Controller
                  name="companyAdminEmail"
                  control={control}
                  render={() => (
                    <TextField
                      value={data && data.companyAdminEmail}
                      disabled
                      label="Company admin email"
                    />
                  )}
                />
                <Controller
                  name="permissionLevel"
                  control={control}
                  render={() => (
                    <TextField
                      value={permissionLevel}
                      disabled
                      label="Company freight permission"
                    />
                  )}
                />
                <Controller
                  name="userFreightPermission"
                  control={control}
                  render={() => (
                    <TextField
                      value={'No Freight'}
                      disabled
                      label="User freight permission"
                    />
                  )}
                />
              </Box>
              <Box sx={{ width: '337px' }}>
                <Controller
                  name="comments"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 250,
                      message: 'error',
                    },
                  }}
                  render={({
                    field: { onChange, value, ref },
                    fieldState: { error },
                  }) => (
                    <MultiLineTextField
                      minRows={10}
                      maxRows={10}
                      label="Comments"
                      onChange={onChange}
                      value={value}
                      inputRef={ref}
                      error={!!error}
                      placeholder={`I’d like to receive the upgraded permission for ${permissionLevel} because |`}
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 251 },
                      }}
                      sx={multiLineStyles}
                    />
                  )}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={helperText(errorCommentsLength)}>
                    Optional
                  </Typography>
                  <Typography sx={helperText(errorCommentsLength)}>
                    {watchComments.length || 0}/250
                  </Typography>
                </Box>
              </Box>
            </UserForm>
            <Box sx={buttonsContainer}>
              <Button variant="outlined" onClick={handleDialogOnClosed}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={
                  !loading && <Icon name="SendRight" alt="Arrow Send Right" />
                }
                type="submit"
                loading={loading}
                sx={{ width: '149px' }}
                disabled={errorCommentsLength}
              >
                Send request
              </Button>
            </Box>
          </form>
        </DialogContent>
      ) : (
        <DialogContent>
          <Box sx={{ display: 'flex', gap: '24px' }}>
            <Box sx={{ width: '240px', height: '240px' }}>
              <Icon name="Glass" alt="Glass Icon" />
            </Box>
            <Box sx={{ margin: 'auto 0' }}>
              <Typography fontSize={18}>
                Waiting on your company admin’s approval. <br />
                Contact them directly for updates.
              </Typography>
            </Box>
          </Box>
          <Box sx={buttonsContainer}>
            <Button
              variant="contained"
              sx={{ background: '#0091EA' }}
              onClick={handleDialogOnClosed}
            >
              Done
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UserPermissionsLevelForm;
